import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Search, Shield, ShieldOff, EyeOff, Eye, CheckCircle2, XCircle, Clock, AlertTriangle, ExternalLink, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import AdminVerificationDrive from "../components/AdminVerificationDrive";
import AdminContentFlags from "../components/AdminContentFlags";
import AdminRecoveryStats from "../components/AdminRecoveryStats";
import AdminOtpHealth from "../components/AdminOtpHealth";
import AdminCleanup from "../components/AdminCleanup";
import AdminCaul from "../components/AdminCaul";
import AdminTemples from "../components/AdminTemples";
import AdminCashouts from "../components/AdminCashouts";
import AdminSubscriptions from "../components/AdminSubscriptions";
import AdminUtm from "../components/AdminUtm";
import AmbassadorAdmin from "../components/AmbassadorAdmin";
import AdminSafetyAudit from "../components/AdminSafetyAudit";
import AdminFounderNFTs from "../components/AdminFounderNFTs";
import AdminLandingPages from "../components/AdminLandingPages";
import AdminMedianSubmission from "../components/AdminMedianSubmission";
import AdminMaintenance from "../components/AdminMaintenance";
import AdminGrants from "../components/AdminGrants";
import AdminElderGrants from "../components/AdminElderGrants";

const TABS = [
  { id: "stats", label: "Stats" },
  { id: "users", label: "Users" },
  { id: "posts", label: "Posts" },
  { id: "reports", label: "Reports" },
  { id: "ads", label: "Ads" },
  { id: "rewards", label: "Tokenomics" },
  { id: "drive", label: "Verification Drive" },
  { id: "content-flags", label: "Content Flags" },
  { id: "recovery", label: "Recovery" },
  { id: "otp-health", label: "OTP Health" },
  { id: "caul", label: "CAUL" },
  { id: "temples", label: "Temples" },
  { id: "cashouts", label: "Cashouts" },
  { id: "subscriptions", label: "Subscriptions" },
  { id: "utm", label: "Attribution" },
  { id: "ambassadors", label: "Ambassadors" },
  { id: "grants", label: "Free Grants" },
  { id: "elder-grants", label: "Elder Grants" },
  { id: "founder-nfts", label: "Founder NFTs" },
  { id: "landing-pages", label: "Landing Pages" },
  { id: "median", label: "Submit to Median" },
  { id: "maintenance", label: "Maintenance" },
  { id: "safety-audit", label: "Safety Audit" },
  { id: "cleanup", label: "Cleanup" },
  { id: "integrations", label: "Integrations" },
  { id: "audit", label: "Audit Log" },
];

export default function Admin() {
  const [tab, setTab] = useState("stats");

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <div className="label-mystic">Oversight</div>
          <h1 className="font-serif text-5xl mt-2">Admin Dashboard</h1>
        </div>

        <div className="flex gap-1 border-b border-secondary/15 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.22em] whitespace-nowrap ${tab===t.id ? "text-secondary border-b-2 border-secondary" : "text-foreground/60 hover:text-foreground"}`}
              data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "stats" && <AdminStats />}
        {tab === "users" && <AdminUsers />}
        {tab === "posts" && <AdminPosts />}
        {tab === "reports" && <AdminReports />}
        {tab === "ads" && <AdminAds />}
        {tab === "rewards" && <AdminRewards />}
        {tab === "drive" && <AdminVerificationDrive />}
        {tab === "content-flags" && <AdminContentFlags />}
        {tab === "recovery" && <AdminRecoveryStats />}
        {tab === "otp-health" && <AdminOtpHealth />}
        {tab === "caul" && <AdminCaul />}
        {tab === "temples" && <AdminTemples />}
        {tab === "cashouts" && <AdminCashouts />}
        {tab === "subscriptions" && <AdminSubscriptions />}
        {tab === "utm" && <AdminUtm />}
        {tab === "ambassadors" && <AmbassadorAdmin />}
        {tab === "grants" && <AdminGrants />}
        {tab === "elder-grants" && <AdminElderGrants />}
        {tab === "founder-nfts" && <AdminFounderNFTs />}
        {tab === "landing-pages" && <AdminLandingPages />}
        {tab === "median" && <AdminMedianSubmission />}
        {tab === "maintenance" && <AdminMaintenance />}
        {tab === "safety-audit" && <AdminSafetyAudit />}
        {tab === "cleanup" && <AdminCleanup />}
        {tab === "integrations" && <AdminIntegrations />}
        {tab === "audit" && <AdminAudit />}
      </div>
    </Layout>
  );
}

function AdminStats() {
  const [stats, setStats] = useState({});
  const [funnel, setFunnel] = useState(null);
  const [funnelRange, setFunnelRange] = useState("all");
  const [funnelBusy, setFunnelBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestStats, setDigestStats] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  useEffect(() => {
    api.get("/admin/stats").then(({data})=>setStats(data)).catch(()=>{});
  }, []);
  useEffect(() => {
    api.get("/admin/funnel", { params: { range: funnelRange } })
      .then(({data})=>setFunnel(data)).catch(()=>{});
  }, [funnelRange]);
  const sendTest = async () => {
    if (!testEmail) return;
    try {
      await api.post("/admin/email/test", { to: testEmail });
      toast.success("Test email queued");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sendSnapshot = async (meOnly = false) => {
    setFunnelBusy(true);
    try {
      const { data } = await api.post("/admin/funnel/snapshot", null, { params: { range: funnelRange, meOnly } });
      if (meOnly) {
        toast.success(`Preview sent to your inbox (${data.recipients[0] || "—"})`);
      } else {
        toast.success(`Snapshot emailed to ${data.sent} admin${data.sent === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setFunnelBusy(false); }
  };
  const sendDigest = async (meOnly = false) => {
    setDigestBusy(true);
    try {
      const { data } = await api.post(
        "/admin/seeker-digest/send-now",
        null,
        { params: { meOnly } },
      );
      setDigestStats(data);
      if (data.providers === 0) {
        toast.info("No new practitioners in the last 7 days — nothing to send.");
      } else if (meOnly) {
        toast.success(`Preview queued (${data.providers} practitioners → your inbox)`);
      } else {
        toast.success(
          `Digest sent to ${data.sent} seeker${data.sent === 1 ? "" : "s"} (${data.providers} practitioners)`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send digest");
    } finally { setDigestBusy(false); }
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
        {Object.entries(stats).map(([k,v]) => (
          <div key={k} className="parchment p-5">
            <div className="label-mystic">{k.replace(/([A-Z])/g, ' $1').trim()}</div>
            <div className="font-serif text-4xl text-secondary mt-1">{v}</div>
          </div>
        ))}
      </div>

      {funnel && funnel.stages && (
        <div className="parchment p-6" data-testid="admin-funnel">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
            <div>
              <div className="label-mystic text-primary">Practitioner Onboarding Funnel</div>
              <p className="text-foreground/65 text-sm mt-1">Track where practitioners drop off between signup and their first Direct Payout.</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex border border-secondary/20 rounded-sm overflow-hidden" data-testid="admin-funnel-range-tabs">
                {[
                  { id: "7d", label: "7 days" },
                  { id: "30d", label: "30 days" },
                  { id: "all", label: "All time" },
                ].map((r) => (
                  <button key={r.id} onClick={() => setFunnelRange(r.id)}
                    className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${funnelRange === r.id ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-secondary/10"}`}
                    data-testid={`admin-funnel-range-${r.id}`}>
                    {r.label}
                  </button>
                ))}
              </div>
              <button onClick={() => sendSnapshot(true)} disabled={funnelBusy}
                className="border border-secondary/40 text-secondary/85 hover:bg-secondary/10 px-3 py-2 rounded-sm text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="admin-funnel-snapshot-preview-btn"
                title="Send a preview only to yourself">
                Preview to Me
              </button>
              <button onClick={() => sendSnapshot(false)} disabled={funnelBusy}
                className="border border-secondary text-secondary hover:bg-secondary/10 px-3 py-2 rounded-sm text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="admin-funnel-snapshot-btn"
                title="Email this snapshot to all admins now">
                {funnelBusy ? "Sending…" : "Email All Admins"}
              </button>
            </div>
          </div>
          <div className="text-xs text-foreground/55 uppercase tracking-wider mb-4">{funnel.topOfFunnel} top-of-funnel</div>
          <div className="space-y-3">
            {funnel.stages.map((s, idx) => {
              const width = Math.max(2, s.pctOfTop);
              const isDrop = idx > 0 && s.pctOfPrev < 60;
              // Day-over-day pp delta — only shown when comparing windowed ranges
              let deltaBadge = null;
              if (typeof s.deltaPp === "number") {
                const isUp = s.deltaPp >= 0.1;
                const isDown = s.deltaPp <= -0.1;
                const color = isUp ? "text-emerald-500/85" : isDown ? "text-destructive/85" : "text-foreground/45";
                const arrow = isUp ? "▲" : isDown ? "▼" : "—";
                const val = Math.abs(s.deltaPp);
                deltaBadge = (
                  <span className={`ml-3 text-xs uppercase tracking-wider ${color}`} data-testid={`admin-funnel-${s.id}-delta`}>
                    {arrow}{val}pp
                  </span>
                );
              }
              return (
                <div key={s.id} className="space-y-1" data-testid={`admin-funnel-${s.id}`}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-foreground/85 font-serif text-lg">{idx + 1}. {s.label}</span>
                    <span className="text-foreground/65">
                      <span className="font-serif text-secondary text-2xl mr-2" data-testid={`admin-funnel-${s.id}-count`}>{s.count.toLocaleString()}</span>
                      <span className="text-xs">{s.pctOfTop}%</span>
                      {idx > 0 && (
                        <span className={`ml-3 text-xs uppercase tracking-wider ${isDrop ? "text-destructive/85" : "text-emerald-500/75"}`}>
                          {s.pctOfPrev}% conv
                        </span>
                      )}
                      {deltaBadge}
                    </span>
                  </div>
                  <div className="w-full h-3 bg-secondary/8 rounded-sm overflow-hidden">
                    <div
                      className={`h-full ${isDrop ? "bg-destructive/55" : "bg-primary/65"} transition-all`}
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="parchment p-6" data-testid="admin-seeker-digest">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="label-mystic text-primary">Seeker Weekly Digest · New This Week</div>
            <p className="text-foreground/75 text-sm mt-1 max-w-xl">
              Friday 16:00 UTC auto-send to every Seeker carrying the same carousel of new practitioners they see on <code className="text-secondary">/feed</code>. Use Preview to Me to sanity-check copy before broadcast.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => sendDigest(true)}
              disabled={digestBusy}
              className="border border-secondary/40 text-secondary/85 hover:bg-secondary/10 px-3 py-2 rounded-sm text-xs uppercase tracking-wider disabled:opacity-50"
              data-testid="admin-seeker-digest-preview-btn"
              title="Send a preview only to your inbox"
            >
              Preview to Me
            </button>
            <button
              onClick={() => sendDigest(false)}
              disabled={digestBusy}
              className="border border-secondary text-secondary hover:bg-secondary/10 px-3 py-2 rounded-sm text-xs uppercase tracking-wider disabled:opacity-50"
              data-testid="admin-seeker-digest-send-btn"
              title="Send the digest to every Seeker right now"
            >
              {digestBusy ? "Sending…" : "Send to All Seekers"}
            </button>
          </div>
        </div>
        {digestStats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-secondary/10" data-testid="admin-seeker-digest-stats">
            <div>
              <div className="label-mystic text-foreground/60">Practitioners</div>
              <div className="font-serif text-2xl text-secondary mt-1" data-testid="admin-seeker-digest-providers">{digestStats.providers ?? 0}</div>
            </div>
            <div>
              <div className="label-mystic text-foreground/60">Sent</div>
              <div className="font-serif text-2xl text-secondary mt-1" data-testid="admin-seeker-digest-sent">{digestStats.sent ?? 0}</div>
            </div>
            {typeof digestStats.skipped_prefs === "number" && (
              <div>
                <div className="label-mystic text-foreground/60">Opted out</div>
                <div className="font-serif text-2xl text-foreground/70 mt-1" data-testid="admin-seeker-digest-optouts">{digestStats.skipped_prefs}</div>
              </div>
            )}
            {typeof digestStats.skipped_stamped === "number" && (
              <div>
                <div className="label-mystic text-foreground/60">Already sent</div>
                <div className="font-serif text-2xl text-foreground/70 mt-1" data-testid="admin-seeker-digest-stamped">{digestStats.skipped_stamped}</div>
              </div>
            )}
            {Array.isArray(digestStats.failures) && digestStats.failures.length > 0 && (
              <div className="col-span-2 sm:col-span-4">
                <div className="label-mystic text-red-400">Failures</div>
                <pre className="mt-1 text-xs text-foreground/75 bg-background/40 p-3 rounded-sm overflow-auto max-h-28">{JSON.stringify(digestStats.failures, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="parchment p-6">
        <div className="label-mystic mb-2">Email · Resend</div>
        <p className="text-foreground/75 text-sm mb-3">Send a branded test email to verify your sender configuration. For production deliverability, verify a sending domain in Resend and update <code className="text-secondary">SENDER_EMAIL</code> + <code className="text-secondary">SENDER_NAME</code> env vars.</p>
        <div className="flex gap-2">
          <input value={testEmail} onChange={(e)=>setTestEmail(e.target.value)} placeholder="recipient@example.com"
            className="flex-1 bg-input border border-secondary/20 p-3 rounded-sm" data-testid="admin-email-test-input" />
          <button onClick={sendTest} className="bg-primary text-primary-foreground px-5 py-3 rounded-sm uppercase tracking-wider text-xs" data-testid="admin-email-test-btn">Send</button>
        </div>
      </div>
    </div>
  );
}

function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [editing, setEditing] = useState(null); // user being edited
  const [confirmDel, setConfirmDel] = useState(null); // user to delete

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (role) params.role = role;
    const { data } = await api.get("/admin/moderation/users", { params });
    // Admins to the top so they're never hidden by a long list of CLIENTs.
    const ordered = [...data].sort((a, b) => {
      const order = { ADMIN: 0, PROVIDER: 1, CLIENT: 2 };
      const ra = order[a.role] ?? 9;
      const rb = order[b.role] ?? 9;
      if (ra !== rb) return ra - rb;
      return (b.createdAt || "").localeCompare(a.createdAt || "");
    });
    setRows(ordered);
  };
  useEffect(() => { load(); }, [status, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const suspend = async (uid) => {
    const reason = window.prompt("Reason for suspension?");
    if (reason === null) return;
    try { await api.post(`/admin/users/${uid}/suspend`, { reason }); toast.success("Suspended"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unsuspend = async (uid) => {
    try { await api.post(`/admin/users/${uid}/unsuspend`, {}); toast.success("Unsuspended"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      const { data } = await api.delete(`/admin/users/${confirmDel.id}`);
      toast.success(`Deleted ${confirmDel.email || confirmDel.name} (cascaded across ${Object.keys(data.cascadeCounters || {}).length} collections)`);
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const admins = rows.filter((u) => u.role === "ADMIN");
  const adminCount = admins.length;

  return (
    <div className="space-y-4">
      {adminCount > 0 && (
        <div className="text-xs text-foreground/50 px-1">
          {adminCount} admin{adminCount !== 1 ? "s" : ""} · {rows.length - adminCount} other{rows.length - adminCount !== 1 ? "s" : ""}
        </div>
      )}
      <div className="parchment p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 bg-input border border-secondary/20 rounded-sm px-3">
          <Search size={14} strokeWidth={1.25} className="text-foreground/50" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()}
            placeholder="Search name or email" className="bg-transparent outline-none flex-1 py-2" data-testid="admin-user-search" />
        </div>
        <select value={role} onChange={(e)=>setRole(e.target.value)} className="bg-input border border-secondary/20 px-3 py-2 rounded-sm" data-testid="admin-user-role-filter">
          <option value="">All roles</option>
          <option value="CLIENT">CLIENT</option>
          <option value="PROVIDER">PROVIDER</option>
          <option value="ADMIN">ADMIN</option>
        </select>
        <select value={status} onChange={(e)=>setStatus(e.target.value)} className="bg-input border border-secondary/20 px-3 py-2 rounded-sm" data-testid="admin-user-status-filter">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>
      <div className="parchment divide-y divide-secondary/15">
        {rows.map((u) => (
          <div key={u.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-user-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-serif text-lg">{u.name}</span>
                <span className={`label-mystic ${u.role === "ADMIN" ? "text-secondary" : ""}`}>{u.role}</span>
                {u.suspended && <span className="text-xs px-2 py-0.5 bg-destructive/20 text-destructive rounded-sm uppercase tracking-wider">Suspended</span>}
                {u.verified && <span className="text-xs px-2 py-0.5 bg-secondary/20 text-secondary rounded-sm uppercase tracking-wider">Verified</span>}
                {u.emailVerified === false && <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded-sm uppercase tracking-wider">Unverified email</span>}
              </div>
              <div className="text-sm text-foreground/60">{u.email}</div>
              {u.role === "PROVIDER" && <div className="text-xs text-foreground/50 mt-1">Sub: {u.subscriptionStatus || "—"} · Score: {(u.performanceScore || 0).toFixed(2)}</div>}
              {u.suspendedReason && <div className="text-xs text-destructive/80 mt-1">Reason: {u.suspendedReason}</div>}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setEditing(u)}
                className="border border-secondary/40 text-secondary px-3 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2 hover:bg-secondary/10"
                data-testid={`admin-user-edit-${u.id}`}
              >
                <Pencil size={12} strokeWidth={1.25} /> Edit
              </button>
              {u.role !== "ADMIN" && (
                u.suspended
                  ? <button onClick={() => unsuspend(u.id)} className="border border-secondary text-secondary px-3 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2" data-testid={`admin-user-unsuspend-${u.id}`}>
                      <Shield size={12} strokeWidth={1.25} /> Unsuspend
                    </button>
                  : <button onClick={() => suspend(u.id)} className="border border-destructive text-destructive px-3 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2" data-testid={`admin-user-suspend-${u.id}`}>
                      <ShieldOff size={12} strokeWidth={1.25} /> Suspend
                    </button>
              )}
              <button
                onClick={() => setConfirmDel(u)}
                className="border border-rose-500/40 text-rose-300 px-3 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2 hover:bg-rose-500/10"
                data-testid={`admin-user-delete-${u.id}`}
              >
                <Trash2 size={12} strokeWidth={1.25} /> Delete
              </button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-6 text-foreground/50 italic">No users.</div>}
      </div>

      {editing && (
        <AdminUserEditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-user-delete-modal">
          <div className="border border-rose-500/40 bg-card max-w-md w-full p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-rose-300">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="font-serif text-2xl">Delete user?</h3>
              </div>
              <button onClick={() => setConfirmDel(null)} className="text-foreground/60 hover:text-foreground" data-testid="admin-user-delete-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-foreground/80 text-sm">
              Permanently delete <b className="text-rose-300">{confirmDel.name}</b> ({confirmDel.email}) and cascade-delete their wallet, profile, posts, bookings, services, notifications, and 10+ other downstream rows. <b>This cannot be undone.</b>
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs uppercase tracking-wider border border-secondary/20 text-foreground/70 hover:bg-secondary/10" data-testid="admin-user-delete-cancel">
                Cancel
              </button>
              <button onClick={doDelete} className="px-4 py-2 text-xs uppercase tracking-wider bg-rose-500 text-white hover:bg-rose-600 flex items-center gap-2" data-testid="admin-user-delete-confirm">
                <Trash2 className="w-3 h-3" />
                Delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminUserEditModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [role, setRole] = useState(user.role || "CLIENT");
  const [emailVerified, setEmailVerified] = useState(user.emailVerified !== false);
  const [busy, setBusy] = useState(false);
  const [newPw, setNewPw] = useState("");

  const save = async () => {
    setBusy(true);
    try {
      // Save profile fields
      await api.patch(`/admin/users/${user.id}`, { name, email, role, emailVerified });
      // Optional password reset
      if (newPw.trim()) {
        if (newPw.length < 8) {
          toast.error("Password must be at least 8 characters");
          setBusy(false);
          return;
        }
        await api.post("/admin/users/reset-password", { email, newPassword: newPw });
      }
      toast.success("User updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-user-edit-modal">
      <div className="border border-secondary/30 bg-card max-w-md w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-mystic">Edit user</div>
            <h3 className="font-serif text-2xl mt-1">{user.name}</h3>
          </div>
          <button onClick={onClose} className="text-foreground/60 hover:text-foreground" data-testid="admin-user-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-foreground/60 text-[10px] uppercase tracking-[0.22em] block mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-input border border-secondary/20 px-3 py-2 text-sm" data-testid="admin-user-edit-name" />
          </div>
          <div>
            <label className="text-foreground/60 text-[10px] uppercase tracking-[0.22em] block mb-1">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-input border border-secondary/20 px-3 py-2 text-sm" data-testid="admin-user-edit-email" />
            {email.toLowerCase() !== (user.email || "").toLowerCase() && (
              <p className="text-amber-300 text-[11px] mt-1">User will be able to log in with their old email too (renamed-email fallback).</p>
            )}
          </div>
          <div>
            <label className="text-foreground/60 text-[10px] uppercase tracking-[0.22em] block mb-1">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-input border border-secondary/20 px-3 py-2 text-sm" data-testid="admin-user-edit-role">
              <option value="CLIENT">CLIENT</option>
              <option value="PROVIDER">PROVIDER</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={emailVerified} onChange={(e) => setEmailVerified(e.target.checked)} data-testid="admin-user-edit-email-verified" />
            <span>Email verified</span>
          </label>
          <div>
            <label className="text-foreground/60 text-[10px] uppercase tracking-[0.22em] block mb-1">Reset password (optional)</label>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Leave blank to keep current" className="w-full bg-input border border-secondary/20 px-3 py-2 text-sm" data-testid="admin-user-edit-password" />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider border border-secondary/20 text-foreground/70 hover:bg-secondary/10" data-testid="admin-user-edit-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={busy} className="px-4 py-2 text-xs uppercase tracking-wider bg-secondary text-background hover:bg-secondary/90 disabled:opacity-50" data-testid="admin-user-edit-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminPosts() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const load = async () => {
    const { data } = await api.get("/admin/moderation/posts", { params: { status } });
    setRows(data);
  };
  useEffect(() => { load(); }, [status]);

  const hide = async (pid) => {
    const reason = window.prompt("Reason for hiding this post?");
    if (reason === null) return;
    try { await api.post(`/admin/posts/${pid}/hide`, { reason }); toast.success("Hidden"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unhide = async (pid) => {
    try { await api.post(`/admin/posts/${pid}/unhide`, {}); toast.success("Restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="parchment p-3 flex gap-2" data-testid="admin-post-filters">
        {["all", "visible", "hidden"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm ${status===s?"bg-secondary/20 text-secondary":"text-foreground/60 hover:text-foreground"}`} data-testid={`admin-posts-${s}`}>{s}</button>
        ))}
      </div>
      <div className="space-y-3">
        {rows.map((p) => (
          <div key={p.id} className={`parchment p-5 ${p.hidden ? "border-destructive/30 opacity-75" : ""}`} data-testid={`admin-post-${p.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-serif">{p.user?.name || p.userId}</span>
                  <span className="label-mystic">{p.type}</span>
                  <span className="text-foreground/50">{new Date(p.createdAt).toLocaleString()}</span>
                  {p.hidden && <span className="text-xs px-2 py-0.5 bg-destructive/20 text-destructive rounded-sm uppercase tracking-wider">Hidden</span>}
                </div>
                <p className="mt-2 text-sm text-foreground/85 line-clamp-3">{p.content}</p>
                {p.hiddenReason && <div className="mt-2 text-xs text-destructive/80 italic">Reason: {p.hiddenReason}</div>}
              </div>
              {p.hidden
                ? <button onClick={() => unhide(p.id)} className="border border-secondary text-secondary px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5" data-testid={`admin-post-unhide-${p.id}`}><Eye size={12} strokeWidth={1.25}/> Restore</button>
                : <button onClick={() => hide(p.id)} className="border border-destructive text-destructive px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5" data-testid={`admin-post-hide-${p.id}`}><EyeOff size={12} strokeWidth={1.25}/> Hide</button>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="parchment p-6 text-foreground/50 italic">No posts.</div>}
      </div>
    </div>
  );
}

function AdminAds() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const load = async () => {
    const { data } = await api.get("/admin/moderation/ads", { params: filter ? { status: filter } : {} });
    setRows(data);
  };
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    try { await api.post(`/admin/ads/${id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection?");
    if (reason === null) return;
    try { await api.post(`/admin/ads/${id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="parchment p-3 flex gap-2 overflow-x-auto" data-testid="admin-ads-filters">
        {["", "ACTIVE", "REJECTED", "COMPLETED"].map((s) => (
          <button key={s||"all"} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm ${filter===s?"bg-secondary/20 text-secondary":"text-foreground/60 hover:text-foreground"}`} data-testid={`admin-ads-filter-${s||"all"}`}>{s||"all"}</button>
        ))}
      </div>
      <div className="space-y-3">
        {rows.map((ad) => (
          <div key={ad.id} className="parchment p-5" data-testid={`admin-ad-${ad.id}`}>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-serif text-lg">{ad.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-sm uppercase tracking-wider ${ad.status==="ACTIVE"?"bg-secondary/20 text-secondary":ad.status==="REJECTED"?"bg-destructive/20 text-destructive":"bg-foreground/10 text-foreground/60"}`}>{ad.status}</span>
                </div>
                <div className="text-xs text-foreground/60 mt-1">By {ad.provider?.name || ad.providerId}</div>
                <p className="text-sm text-foreground/80 mt-2 line-clamp-2">{ad.description}</p>
                <div className="text-xs text-foreground/50 mt-2">Budget {ad.budget} CAUL · Spent {ad.spent || 0} · Impressions {ad.impressions || 0} · Clicks {ad.clicks || 0}</div>
                {ad.rejectedReason && <div className="text-xs text-destructive/80 italic mt-1">Reason: {ad.rejectedReason}</div>}
              </div>
              <div className="flex gap-2">
                {ad.status !== "ACTIVE" && <button onClick={() => approve(ad.id)} className="border border-secondary text-secondary px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5" data-testid={`admin-ad-approve-${ad.id}`}><CheckCircle2 size={12} strokeWidth={1.25}/> Approve</button>}
                {ad.status !== "REJECTED" && <button onClick={() => reject(ad.id)} className="border border-destructive text-destructive px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5" data-testid={`admin-ad-reject-${ad.id}`}><XCircle size={12} strokeWidth={1.25}/> Reject</button>}
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="parchment p-6 text-foreground/50 italic">No ads.</div>}
      </div>
    </div>
  );
}

function AdminAudit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit").then(({data})=>setRows(data)).catch(()=>{}); }, []);
  return (
    <div className="parchment divide-y divide-secondary/15" data-testid="admin-audit-log">
      {rows.map((r) => (
        <div key={r.id} className="p-4 flex items-start gap-3" data-testid={`audit-${r.id}`}>
          <Clock size={14} strokeWidth={1.25} className="text-secondary/60 mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm">
              <span className="font-serif">{r.admin?.name || r.adminId}</span>
              <span className="text-foreground/60"> · </span>
              <span className="text-secondary">{r.action}</span>
              <span className="text-foreground/60"> on </span>
              <span className="font-mono text-xs">{r.targetType}/{r.targetId}</span>
            </div>
            {r.reason && <div className="text-xs text-foreground/70 mt-1">{r.reason}</div>}
            <div className="text-xs text-foreground/50 mt-1">{new Date(r.createdAt).toLocaleString()}</div>
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="p-6 text-foreground/50 italic">No audit events yet.</div>}
    </div>
  );
}

function AdminRewards() {
  const [stats, setStats] = useState(null);
  const [banks, setBanks] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/rewards/stats"),
        api.get("/admin/rewards/banks").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setBanks(b.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const backfill = async () => {
    if (!window.confirm("Backfill lockups for all existing users? This is idempotent.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rewards/backfill");
      toast.success(`Granted ${data.granted.signup_client} client / ${data.granted.signup_provider} provider / ${data.granted.elder} elder lockups · ${data.granted.skipped} skipped`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!stats) return <div className="parchment p-8 text-foreground/60 italic">Loading…</div>;
  const KINDS = [
    { id: "signup_client", label: "Client signup (1k)", capKey: "userBonus" },
    { id: "signup_provider", label: "Provider signup (20k)", capKey: "providerBonus" },
    { id: "elder", label: "Elder (10k + badge)", capKey: "elder" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-rewards">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {KINDS.map((k) => {
          const r = stats.byKind?.[k.id] || { count: 0, totalLocked: 0, totalUnlocked: 0 };
          const cap = stats.caps?.[k.capKey] || 0;
          const slots = stats.slotsRemaining?.[k.capKey] || 0;
          const pct = cap ? Math.round(((cap - slots) / cap) * 100) : 0;
          return (
            <div key={k.id} className="parchment p-5" data-testid={`admin-rewards-${k.id}`}>
              <div className="label-mystic text-primary">{k.label}</div>
              <div className="font-serif text-4xl text-secondary mt-1">{r.count.toLocaleString()}</div>
              <div className="text-xs text-foreground/60 mt-1">of {cap.toLocaleString()} · {slots.toLocaleString()} slots left ({pct}% filled)</div>
              <div className="text-xs text-foreground/55 mt-2 border-t border-secondary/15 pt-2 space-y-1">
                <div>Total granted: <b>{r.totalLocked.toLocaleString()} CAUL</b></div>
                <div>Unlocked so far: <b>{r.totalUnlocked.toLocaleString()} CAUL</b></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="parchment p-6">
        <div className="label-mystic mb-2">One-shot Backfill</div>
        <p className="text-foreground/75 text-sm mb-3">Grants signup lockups to users who joined before tokenomics shipped (idempotent — skips users who already have a lockup).</p>
        <button onClick={backfill} disabled={busy}
          className="bg-primary text-primary-foreground px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
          data-testid="admin-rewards-backfill-btn">
          {busy ? "Backfilling…" : "Backfill Lockups Now"}
        </button>
      </div>

      {banks && (
        <div className="space-y-3" data-testid="admin-banks-tile">
          <div className="parchment p-5">
            <div className="label-mystic text-primary">Direct Payouts · Banks Attached</div>
            <div className="flex items-baseline gap-3 mt-2 flex-wrap">
              <div className="font-serif text-4xl text-secondary" data-testid="admin-banks-total">{banks.total.toLocaleString()}</div>
              <div className="text-sm text-foreground/65">
                {Object.entries(banks.byType).map(([k, v]) => `${v} ${k === "bank_account" ? "bank" : k}${v === 1 ? "" : "s"}`).join(" · ")}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="parchment p-5">
              <div className="label-mystic mb-3">Top Banks</div>
              {banks.topBanks.length === 0 ? (
                <div className="text-foreground/55 italic text-sm">No banks attached yet.</div>
              ) : (
                <ol className="space-y-1.5">
                  {banks.topBanks.map((b, idx) => (
                    <li key={b.name} className="flex justify-between items-baseline text-sm" data-testid={`admin-bank-${idx}`}>
                      <span className="text-foreground/85">{idx + 1}. {b.name}</span>
                      <span className="font-serif text-secondary">{b.count}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="parchment p-5">
              <div className="label-mystic mb-3">Last 12 Months</div>
              <div className="flex items-end gap-1 h-24">
                {banks.byMonth.map((m) => {
                  const max = Math.max(1, ...banks.byMonth.map((x) => x.count));
                  const h = (m.count / max) * 100;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${m.month} · ${m.count}`}>
                      <div className="w-full bg-primary/30 hover:bg-primary/60 transition-colors rounded-sm-t" style={{ height: `${h}%`, minHeight: m.count > 0 ? "4px" : "0" }} />
                      <div className="text-[9px] text-foreground/45 font-mono">{m.month.slice(-2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {banks.recent.length > 0 && (
            <div className="parchment p-5">
              <div className="label-mystic mb-3">Recent Attachments</div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {banks.recent.map((r) => (
                  <div key={r.id || `${r.stripeAccountId}-${r.createdAt}`} className="flex justify-between items-baseline text-sm border-b border-secondary/10 pb-1.5" data-testid={`admin-bank-recent-${r.id}`}>
                    <span className="text-foreground/85">
                      {r.object === "bank_account" ? "Bank" : "Card"} · {r.bankName || "—"} ****{r.last4 || "????"}
                    </span>
                    <span className="text-xs text-foreground/55 font-mono">{r.createdAt?.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function AdminIntegrations() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/integrations/health").then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  }, []);

  if (!data) return <div className="parchment p-8 text-foreground/60 italic">Probing integrations…</div>;

  const ROWS = [
    {
      id: "resend",
      label: "Resend · Transactional Email",
      status: data.resend,
      doc: "https://resend.com/domains",
      docLabel: "Resend Dashboard",
    },
    {
      id: "stripe",
      label: "Stripe · Payments + Webhooks",
      status: data.stripe,
      doc: "https://dashboard.stripe.com",
      docLabel: "Stripe Dashboard",
    },
    {
      id: "stripeConnect",
      label: "Stripe Connect · Direct Payouts",
      status: data.stripeConnect,
      doc: "https://dashboard.stripe.com/settings/connect/platform-profile",
      docLabel: "Activate Connect",
    },
    {
      id: "twilioVideo",
      label: "Twilio Video · Live Rooms",
      status: data.twilioVideo,
      doc: "https://console.twilio.com",
      docLabel: "Twilio Console",
    },
  ];

  return (
    <div className="space-y-4" data-testid="admin-integrations">
      <div className="parchment p-5">
        <div className="label-mystic mb-1">External Integration Health</div>
        <p className="text-foreground/70 text-sm">Per-integration status. Yellow = configured but needs external activation. Green = healthy. Red = unconfigured.</p>
      </div>

      {ROWS.map((row) => {
        const ok = row.status?.ok;
        const warning = row.status?.warning;
        const color = ok ? "text-emerald-400" : warning ? "text-primary" : "text-destructive";
        const Icon = ok ? CheckCircle2 : warning ? AlertTriangle : XCircle;
        return (
          <div key={row.id} className="parchment p-5 flex items-start justify-between gap-4 flex-wrap" data-testid={`admin-integration-${row.id}`}>
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <Icon size={22} strokeWidth={1.75} className={`${color} mt-1 shrink-0`} />
              <div className="flex-1">
                <div className="label-mystic">{row.label}</div>
                <div className="mt-1 font-serif text-xl">
                  {ok ? "Healthy" : warning ? "Action Required" : "Not Configured"}
                </div>
                {warning && <div className="text-xs text-foreground/65 mt-2 leading-relaxed max-w-2xl">{warning}</div>}
                <div className="text-xs text-foreground/45 mt-2 font-mono">
                  {Object.entries(row.status || {})
                    .filter(([k]) => k !== "warning" && k !== "setupDoc")
                    .map(([k, v]) => `${k}=${typeof v === "boolean" ? (v ? "✓" : "✗") : v || "—"}`)
                    .join("  ·  ")}
                </div>
              </div>
            </div>
            <a href={row.doc} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-secondary/40 hover:border-secondary text-secondary px-4 py-2 rounded-sm text-xs uppercase tracking-wider whitespace-nowrap"
              data-testid={`admin-integration-${row.id}-link`}>
              {row.docLabel} <ExternalLink size={12} strokeWidth={1.5} />
            </a>
          </div>
        );
      })}

      <div className="parchment p-5 border-secondary/20 bg-secondary/[0.03]">
        <div className="label-mystic mb-2">Setup Guide</div>
        <p className="text-foreground/70 text-sm">
          The full step-by-step guide for activating Resend (DNS records) and Stripe Connect (platform profile)
          is stored at <code className="text-secondary">/app/memory/INTEGRATION_SETUP.md</code>.
        </p>
      </div>
    </div>
  );
}


function AdminReports() {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/moderation/reports?status=${s}`);
      setReports(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const resolveReport = async (r) => {
    if (!window.confirm(`Mark this ${r.reason} report as resolved?`)) return;
    try {
      await api.post(`/admin/moderation/reports/${r.id}/resolve`, { reason: "Dismissed by admin" });
      toast.success("Report resolved");
      setReports((arr) => arr.filter((x) => x.id !== r.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const hidePost = async (postId) => {
    if (!postId) return;
    if (!window.confirm("Hide this post from the public feed?")) return;
    try {
      await api.post(`/admin/posts/${postId}/hide`, { reason: "Hidden via report" });
      toast.success("Post hidden");
      // Refresh — posts may still appear via report list, that's fine
      load(status);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-reports-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-mystic">User-filed reports</div>
          <p className="text-sm text-foreground/65">Posts flagged by community members for review.</p>
        </div>
        <div className="inline-flex border border-secondary/20 rounded-sm overflow-hidden" data-testid="admin-reports-status-tabs">
          {["open", "resolved", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-4 py-2 text-xs uppercase tracking-wider ${status === s ? "bg-secondary/20 text-secondary" : "text-foreground/60 hover:text-foreground"}`}
              data-testid={`admin-reports-status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="parchment p-8 text-center text-foreground/60">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="parchment p-8 text-center text-foreground/60" data-testid="admin-reports-empty">
          No reports {status === "open" ? "open" : status === "resolved" ? "resolved yet" : ""}.
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div
              key={r.id}
              className="parchment p-4 flex flex-col sm:flex-row sm:items-start gap-4"
              data-testid={`admin-report-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-sm bg-destructive/15 text-destructive uppercase tracking-wider font-bold" data-testid={`admin-report-reason-${r.id}`}>
                    {r.reason}
                  </span>
                  {r.status === "resolved" && (
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-emerald-900/30 text-emerald-300 uppercase tracking-wider">
                      Resolved
                    </span>
                  )}
                  <span className="text-xs text-foreground/55">
                    by {r.reporterName || "unknown"} · {new Date(r.createdAt).toLocaleString()}
                  </span>
                </div>
                {r.note && <p className="text-sm text-foreground/85 mb-2">"{r.note}"</p>}
                <div className="text-xs text-foreground/60 mb-2">
                  Author: <span className="text-secondary">{r.postAuthor?.name || r.postAuthorId}</span>
                </div>
                {r.post ? (
                  <div className="bg-secondary/[0.03] border border-secondary/15 p-3 rounded-sm">
                    <div className="label-mystic mb-1">{r.post.type} {r.post.hidden ? "· hidden" : ""}</div>
                    <p className="text-sm text-foreground/90 line-clamp-3">{r.post.content || <em className="text-foreground/40">(no text — media post)</em>}</p>
                  </div>
                ) : (
                  <div className="text-xs text-foreground/40 italic">(Post already deleted)</div>
                )}
              </div>
              <div className="flex sm:flex-col gap-2 shrink-0">
                {r.status !== "resolved" && (
                  <>
                    {r.post && !r.post.hidden && (
                      <button
                        onClick={() => hidePost(r.postId)}
                        className="text-xs px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm uppercase tracking-wider hover:bg-destructive/10"
                        data-testid={`admin-report-hide-post-${r.id}`}
                      >
                        Hide post
                      </button>
                    )}
                    <button
                      onClick={() => resolveReport(r)}
                      className="text-xs px-3 py-1.5 border border-secondary/30 text-foreground/80 rounded-sm uppercase tracking-wider hover:bg-secondary/10"
                      data-testid={`admin-report-resolve-${r.id}`}
                    >
                      Dismiss
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
